import { Box, Flex, Icon, Spinner, Text, VStack } from "@chakra-ui/react";
import { RiCheckboxCircleFill } from "react-icons/ri";
import { Glossary } from "src/components/Text/Glossary";
import type { ApplicantDuplicate } from "src/generated/hooks/form-service/requests";
import { useOrganization } from "src/hooks/useOrganization";
import { formatBirthDate } from "src/services/format";
import { DuplicateStudentCard } from "./DuplicateStudentCard";
import { useStudentDuplicateSearch } from "./useStudentDuplicateSearch";

interface PossibleDuplicatesPanelProps {
  organizationId: string;
  // Select a matched student in place instead of navigating to its edit page.
  onUseStudent: (applicant: ApplicantDuplicate) => void;
}

const PARENT_CHECK_COPY =
  "Parents are checked against email and phone once either is entered.";

export function PossibleDuplicatesPanel({
  organizationId,
  onUseStudent,
}: PossibleDuplicatesPanelProps) {
  const organization = useOrganization();
  const {
    isQualified,
    isLoading,
    isError,
    hasSearched,
    matches,
    similar,
    fullName,
    birthDate,
  } = useStudentDuplicateSearch(organizationId);

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      backgroundColor="gray.50"
      padding={5}
      width="100%"
    >
      <Text
        textTransform="uppercase"
        letterSpacing="wider"
        fontSize="sm"
        fontWeight="700"
        color="gray.500"
        marginBottom={4}
      >
        <Glossary>Possible duplicates</Glossary>
      </Text>
      {renderBody()}
    </Box>
  );

  function renderBody() {
    if (!isQualified) {
      return (
        <Text fontSize="sm" color="gray.500">
          <Glossary>
            {`Students are checked against date of birth once they're entered. ${PARENT_CHECK_COPY}`}
          </Glossary>
        </Text>
      );
    }

    if (isError) {
      return (
        <Text fontSize="sm" color="red.600">
          Something went wrong checking for duplicates. Please try again.
        </Text>
      );
    }

    if (isLoading || !hasSearched) {
      return (
        <Flex justifyContent="center" paddingY={4}>
          <Spinner />
        </Flex>
      );
    }

    if (matches.length > 0) {
      return (
        <Flex direction="column" gap={4}>
          <VStack align="stretch" spacing={3} maxHeight="60vh" overflowY="auto">
            {matches.map((applicant) => (
              <DuplicateStudentCard
                key={applicant.id}
                applicant={applicant}
                organization={organization}
                onUse={onUseStudent}
              />
            ))}
          </VStack>
          {renderParentCheckCopy()}
        </Flex>
      );
    }

    return (
      <Flex direction="column" gap={4}>
        {renderNoMatch()}
        {renderParentCheckCopy()}
      </Flex>
    );
  }

  function renderNoMatch() {
    const born = formatBirthDate(birthDate);
    return (
      <Flex direction="column" gap={3}>
        <Flex align="flex-start" gap={2}>
          <Icon
            as={RiCheckboxCircleFill}
            color="green.500"
            boxSize={5}
            flexShrink={0}
          />
          <Text fontSize="sm" color="gray.700">
            <Text as="span" fontWeight="700">
              <Glossary>Student</Glossary>:
            </Text>{" "}
            {`no record matches ${fullName}, born ${born}`}
          </Text>
        </Flex>
        <Box
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          backgroundColor="white"
          padding={4}
        >
          <Text fontSize="sm" color="gray.700">
            Checked with name + date of birth.
          </Text>
          {similar.length > 0 && (
            <>
              <Text fontSize="sm" color="gray.700" marginTop={2}>
                Similar names checked:
              </Text>
              {similar.map((applicant) => {
                const name = [applicant.first_name, applicant.last_name]
                  .filter(Boolean)
                  .join(" ");
                const applicantBorn = applicant.birth_date
                  ? formatBirthDate(applicant.birth_date)
                  : null;
                return (
                  <Text
                    key={applicant.id}
                    fontSize="sm"
                    color="gray.700"
                    marginTop={2}
                  >
                    {name}
                    {applicantBorn ? ` (born ${applicantBorn})` : ""}:{" "}
                    <Text as="span" fontWeight="700">
                      not a match
                    </Text>
                    , different date of birth
                  </Text>
                );
              })}
            </>
          )}
        </Box>
      </Flex>
    );
  }

  function renderParentCheckCopy() {
    return (
      <Text fontSize="sm" color="gray.500">
        <Glossary>{PARENT_CHECK_COPY}</Glossary>
      </Text>
    );
  }
}
